import { getAvailability, type WeekAvailability } from '@pa/api/public'
import { isApiError, type ApiErrorCode } from '@pa/api'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { HOLD_TIMEOUT_MS } from './usePatientApi'

export interface WeekQuery {
  locationId: string
  visitTypeId: string
  providerId: string
  weekStart: string
}

/** D-44: P-08 refetches the shown week this often while it is shown with no hold. */
export const POLL_MS = 20_000
/** Frontend §3.3: availability is fresh for 15 s. */
const STALE_MS = 15_000
/** Weeks seen in this visit stay cached for a while, so going back to one (or from P-09 to P-08) needs no spinner. */
const GC_MS = 5 * 60_000

/**
 * #7 one week of open times (API.md §4.7), fetched per week (UserFlows §2.4), with the patient read policy (one
 * jittered retry for network errors, timeouts and 500s; Frontend §3.3) and a 10 s timeout (§4.8).
 * - `poll`: refetch every 20 s, only while P-08 is shown with no hold (D-44). TanStack Query pauses the interval while
 *   the tab is hidden, and a return to the tab refetches at once.
 * - `reload`: a bump refetches the same week in place (after a lost hold or booking), without the loading state.
 * Errors come back as API codes for the machine.
 */
export function useWeek(slug: string, q: WeekQuery | null, reload: number, poll: boolean) {
  const query = useQuery<WeekAvailability, unknown>({
    queryKey: ['availability', slug, q],
    queryFn: ({ signal }) =>
      getAvailability(slug, q ?? { locationId: '', visitTypeId: '', providerId: '', weekStart: '' }, {
        timeoutMs: HOLD_TIMEOUT_MS,
        signal,
      }),
    enabled: q !== null,
    staleTime: STALE_MS,
    gcTime: GC_MS,
    refetchInterval: poll ? POLL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: poll ? 'always' : false,
  })
  const { refetch } = query
  const enabled = q !== null
  useEffect(() => {
    if (reload > 0 && enabled) void refetch()
  }, [reload, enabled, refetch])
  return query
}

/** The API code of a failed query (`network_error` for anything that isn't an API error). */
export const codeOf = (error: unknown): ApiErrorCode => (isApiError(error) ? error.code : 'network_error')
